/**
 * Plattform-Nachrichtencenter (P2.7 Tenant-Admin-Center) — Operator-Seite.
 *
 * Typen, feste Auswahllisten, API-Aufrufe und reine Helfer (Formular ->
 * Payload, Zielgruppen-Zusammenfassung, Status) fuer die Seite
 * `PlatformMessagesPage`. Die Helfer sind bewusst frei von React, damit sie
 * ohne DOM-Testumgebung (vitest, node) geprueft werden koennen.
 *
 * Backend (Vertrag p27): GET /api/platform-messages (requireReadOnly),
 * POST /api/platform-messages, PUT/DELETE /api/platform-messages/:id
 * (requireOperator). Audit: platform.message.created|updated|deleted.
 */
import api from './api';

export type MessageCategory = 'maintenance' | 'incident' | 'change' | 'info';
export type MessageSeverity = 'info' | 'warning' | 'critical';
export type MessageAudience = 'all' | 'plans' | 'tenants';

/** Nachricht in der Operator-Sicht (TenantMessage ohne `read`, plus Verwaltungsfelder). */
export interface PlatformMessage {
  id: string;
  category: MessageCategory;
  severity: MessageSeverity;
  title: string;
  /** Klartext (kein HTML), Zeilenumbrueche erlaubt. */
  body: string;
  affectedServices: string[];
  startsAt: string | null;
  endsAt: string | null;
  actionRequired: boolean;
  audience: MessageAudience;
  audiencePlans: string[];
  audienceTenants: string[];
  publishedAt: string;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Anzahl Mandanten, die die Nachricht gelesen haben. */
  readCount: number;
}

/** Schreib-Payload fuer POST/PUT. */
export interface PlatformMessageInput {
  category: MessageCategory;
  severity: MessageSeverity;
  title: string;
  body: string;
  affectedServices: string[];
  startsAt: string | null;
  endsAt: string | null;
  actionRequired: boolean;
  audience: MessageAudience;
  audiencePlans: string[];
  audienceTenants: string[];
  /** Fehlt es, setzt das Backend "jetzt". */
  publishedAt?: string;
  expiresAt: string | null;
}

export const MESSAGE_CATEGORIES: MessageCategory[] = ['maintenance', 'incident', 'change', 'info'];
export const MESSAGE_SEVERITIES: MessageSeverity[] = ['info', 'warning', 'critical'];

export const CATEGORY_LABEL: Record<MessageCategory, string> = {
  maintenance: 'Wartung',
  incident: 'Störung',
  change: 'Änderung',
  info: 'Information',
};

export const SEVERITY_LABEL: Record<MessageSeverity, string> = {
  info: 'Info',
  warning: 'Warnung',
  critical: 'Kritisch',
};

/** Feste Dienst-IDs aus service-health (Vertrag p27, Abschnitt B). */
export const SERVICE_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'login', label: 'Anmeldung' },
  { id: 'mail', label: 'E-Mail' },
  { id: 'files', label: 'Dateien (Nextcloud)' },
  { id: 'chat', label: 'Chat (Matrix)' },
  { id: 'webmail', label: 'Webmail' },
  { id: 'time', label: 'Zeiterfassung' },
  { id: 'desktop', label: 'Virtueller Desktop' },
  { id: 'portal', label: 'Intranet-Portal' },
  { id: 'admin', label: 'Admin-Portal' },
];

export const PLAN_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'basic', label: 'Basic' },
  { id: 'business', label: 'Business' },
  { id: 'enterprise', label: 'Enterprise' },
];

export function labelForCategory(c: string): string {
  return CATEGORY_LABEL[c as MessageCategory] ?? c;
}

export function labelForSeverity(s: string): string {
  return SEVERITY_LABEL[s as MessageSeverity] ?? s;
}

export function labelForService(id: string): string {
  return SERVICE_OPTIONS.find((s) => s.id === id)?.label ?? id;
}

export function labelForPlan(id: string): string {
  return PLAN_OPTIONS.find((p) => p.id === id)?.label ?? id;
}

// ---------------------------------------------------------------------------
// Datum/Zeit: <input type="datetime-local"> <-> ISO-8601
// ---------------------------------------------------------------------------

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * ISO-Zeitstempel -> Wert fuer datetime-local (lokale Zeit, Minutengenau).
 * Leere/ungueltige Eingaben ergeben ''.
 */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * datetime-local-Wert (lokale Zeit) -> ISO-8601 (UTC). Leer/ungueltig -> null.
 * Parsen explizit ueber die Komponenten, damit keine Engine den Wert
 * versehentlich als UTC interpretiert.
 */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), s ? Number(s) : 0);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

// ---------------------------------------------------------------------------
// Formular-Modell
// ---------------------------------------------------------------------------

/** Rohzustand des Formulars (alle Zeitfelder als datetime-local-Strings). */
export interface MessageFormState {
  category: MessageCategory;
  severity: MessageSeverity;
  title: string;
  body: string;
  affectedServices: string[];
  startsAt: string;
  endsAt: string;
  actionRequired: boolean;
  audience: MessageAudience;
  audiencePlans: string[];
  /** Kommagetrennte Mandanten-Slugs. */
  audienceTenants: string;
  publishedAt: string;
  expiresAt: string;
}

export function emptyMessageForm(now: Date = new Date()): MessageFormState {
  return {
    category: 'info',
    severity: 'info',
    title: '',
    body: '',
    affectedServices: [],
    startsAt: '',
    endsAt: '',
    actionRequired: false,
    audience: 'all',
    audiencePlans: [],
    audienceTenants: '',
    publishedAt: isoToLocalInput(now.toISOString()),
    expiresAt: '',
  };
}

export function messageToForm(msg: PlatformMessage): MessageFormState {
  return {
    category: msg.category,
    severity: msg.severity,
    title: msg.title,
    body: msg.body,
    affectedServices: [...(msg.affectedServices ?? [])],
    startsAt: isoToLocalInput(msg.startsAt),
    endsAt: isoToLocalInput(msg.endsAt),
    actionRequired: !!msg.actionRequired,
    audience: msg.audience ?? 'all',
    audiencePlans: [...(msg.audiencePlans ?? [])],
    audienceTenants: (msg.audienceTenants ?? []).join(', '),
    publishedAt: isoToLocalInput(msg.publishedAt),
    expiresAt: isoToLocalInput(msg.expiresAt),
  };
}

/** Kommagetrennte (auch Leerzeichen/Zeilenumbruch) Slug-Liste -> bereinigtes, eindeutiges Array. */
export function parseSlugList(text: string): string[] {
  const seen = new Set<string>();
  for (const raw of text.split(/[\s,;]+/)) {
    const slug = raw.trim().toLowerCase();
    if (slug) seen.add(slug);
  }
  return [...seen];
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** Fuegt einen Slug zur kommagetrennten Liste hinzu bzw. entfernt ihn. */
export function toggleSlugInList(text: string, slug: string): string {
  const list = parseSlugList(text);
  const next = list.includes(slug) ? list.filter((s) => s !== slug) : [...list, slug];
  return next.join(', ');
}

/** Validiert das Formular; liefert deutsche Fehlermeldungen (leer = gueltig). */
export function validateMessageForm(form: MessageFormState): string[] {
  const errors: string[] = [];
  if (!form.title.trim()) errors.push('Titel ist erforderlich.');
  if (form.title.trim().length > 200) errors.push('Titel darf höchstens 200 Zeichen lang sein.');
  if (!form.body.trim()) errors.push('Text ist erforderlich.');

  const startsAt = localInputToIso(form.startsAt);
  const endsAt = localInputToIso(form.endsAt);
  if (form.startsAt && !startsAt) errors.push('Beginn ist kein gültiges Datum.');
  if (form.endsAt && !endsAt) errors.push('Ende ist kein gültiges Datum.');
  if (startsAt && endsAt && new Date(endsAt) < new Date(startsAt)) {
    errors.push('Ende muss nach dem Beginn liegen.');
  }

  const publishedAt = localInputToIso(form.publishedAt);
  const expiresAt = localInputToIso(form.expiresAt);
  if (form.publishedAt && !publishedAt) errors.push('Veröffentlichung ist kein gültiges Datum.');
  if (form.expiresAt && !expiresAt) errors.push('Ablauf ist kein gültiges Datum.');
  if (expiresAt && new Date(expiresAt) <= new Date(publishedAt ?? Date.now())) {
    errors.push('Ablauf muss nach der Veröffentlichung liegen.');
  }

  if (form.audience === 'plans' && form.audiencePlans.length === 0) {
    errors.push('Mindestens einen Plan auswählen.');
  }
  if (form.audience === 'tenants') {
    const slugs = parseSlugList(form.audienceTenants);
    if (slugs.length === 0) errors.push('Mindestens einen Mandanten angeben.');
    const invalid = slugs.filter((s) => !SLUG_RE.test(s));
    if (invalid.length > 0) errors.push(`Ungültige Mandanten-Slugs: ${invalid.join(', ')}`);
  }
  return errors;
}

/**
 * Formular -> API-Payload. Zeitfelder werden nach ISO konvertiert; die
 * jeweils nicht gewaehlte Zielgruppen-Liste wird geleert, damit keine
 * Altlasten (z. B. Plaene bei audience=tenants) gespeichert werden.
 */
export function formToPayload(form: MessageFormState): PlatformMessageInput {
  const payload: PlatformMessageInput = {
    category: form.category,
    severity: form.severity,
    title: form.title.trim(),
    body: form.body.replace(/\r\n/g, '\n').trim(),
    // Nur bekannte Dienst-IDs, in fester Reihenfolge.
    affectedServices: SERVICE_OPTIONS.map((s) => s.id).filter((id) => form.affectedServices.includes(id)),
    startsAt: localInputToIso(form.startsAt),
    endsAt: localInputToIso(form.endsAt),
    actionRequired: form.actionRequired,
    audience: form.audience,
    audiencePlans: form.audience === 'plans'
      ? PLAN_OPTIONS.map((p) => p.id).filter((id) => form.audiencePlans.includes(id))
      : [],
    audienceTenants: form.audience === 'tenants' ? parseSlugList(form.audienceTenants) : [],
    expiresAt: localInputToIso(form.expiresAt),
  };
  const publishedAt = localInputToIso(form.publishedAt);
  if (publishedAt) payload.publishedAt = publishedAt;
  return payload;
}

// ---------------------------------------------------------------------------
// Anzeige-Helfer
// ---------------------------------------------------------------------------

type AudienceFields = Pick<PlatformMessage, 'audience' | 'audiencePlans' | 'audienceTenants'>;

/** Kurze deutsche Zusammenfassung der Zielgruppe fuer die Liste. */
export function audienceSummary(msg: AudienceFields, maxTenants = 3): string {
  if (msg.audience === 'plans') {
    const plans = msg.audiencePlans ?? [];
    if (plans.length === 0) return 'Pläne: —';
    return `Pläne: ${plans.map(labelForPlan).join(', ')}`;
  }
  if (msg.audience === 'tenants') {
    const tenants = msg.audienceTenants ?? [];
    if (tenants.length === 0) return 'Mandanten: —';
    const shown = tenants.slice(0, maxTenants).join(', ');
    const rest = tenants.length - maxTenants;
    return `Mandanten (${tenants.length}): ${shown}${rest > 0 ? ` +${rest}` : ''}`;
  }
  return 'Alle Mandanten';
}

export function readCountLabel(n: number | null | undefined): string {
  const count = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  return `gelesen von ${count} ${count === 1 ? 'Mandant' : 'Mandanten'}`;
}

export type PublicationStatus = 'scheduled' | 'active' | 'expired';

export const PUBLICATION_STATUS_LABEL: Record<PublicationStatus, string> = {
  scheduled: 'Geplant',
  active: 'Sichtbar',
  expired: 'Abgelaufen',
};

/** Sichtbarkeit analog zur Mandanten-Sicht: publishedAt <= jetzt < expiresAt. */
export function publicationStatus(
  msg: Pick<PlatformMessage, 'publishedAt' | 'expiresAt'>,
  now: Date = new Date(),
): PublicationStatus {
  const t = now.getTime();
  if (msg.expiresAt && new Date(msg.expiresAt).getTime() <= t) return 'expired';
  if (msg.publishedAt && new Date(msg.publishedAt).getTime() > t) return 'scheduled';
  return 'active';
}

/** Datum/Uhrzeit (de-DE) oder Gedankenstrich. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Listen-Antwort normalisieren: der Vertrag legt die Huelle der
 * Operator-Liste nicht fest; akzeptiert werden ein nacktes Array sowie
 * `{ items }` (wie die Mandanten-Sicht) und `{ rows }` (wie /tenants).
 */
export function unwrapMessageList(data: unknown): PlatformMessage[] {
  if (Array.isArray(data)) return data as PlatformMessage[];
  if (data && typeof data === 'object') {
    const obj = data as { items?: unknown; rows?: unknown; messages?: unknown };
    for (const candidate of [obj.items, obj.rows, obj.messages]) {
      if (Array.isArray(candidate)) return candidate as PlatformMessage[];
    }
  }
  return [];
}

/** Einzelobjekt-Antwort normalisieren (`{ message }` oder nackt). */
function unwrapMessage(data: unknown): PlatformMessage {
  if (data && typeof data === 'object' && 'message' in data) {
    const inner = (data as { message: unknown }).message;
    if (inner && typeof inner === 'object') return inner as PlatformMessage;
  }
  return data as PlatformMessage;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listPlatformMessages(): Promise<PlatformMessage[]> {
  const r = await api.get('/platform-messages');
  return unwrapMessageList(r.data);
}

export async function createPlatformMessage(input: PlatformMessageInput): Promise<PlatformMessage> {
  const r = await api.post('/platform-messages', input);
  return unwrapMessage(r.data);
}

export async function updatePlatformMessage(id: string, input: PlatformMessageInput): Promise<PlatformMessage> {
  const r = await api.put(`/platform-messages/${encodeURIComponent(id)}`, input);
  return unwrapMessage(r.data);
}

export async function deletePlatformMessage(id: string): Promise<void> {
  await api.delete(`/platform-messages/${encodeURIComponent(id)}`);
}
